/**
 * Required permission to access a route. All grant data comes from GET /users/me (data.roleId.permissions).
 * - module: required (must match API). action optional: if omitted, any permission in that module grants access (dynamic when API adds/removes actions).
 */
export interface RoutePermission {
  module: string;
  action?: string;
  /** Any-of semantics: access granted if the user has at least one of these actions in the module. */
  actions?: string[];
  /** All-of semantics: access granted only when every nested permission is granted. */
  allOf?: RoutePermission[];
}

/**
 * Maps route path to the permission required to view it. Access is decided only from the API response:
 * if the user's roleId.permissions from GET /users/me includes the module (and action when set), they can access the route.
 * /settings is omitted so every user can access profile/security/sessions; the guard allows /settings without a check.
 * When the backend changes permission names or adds modules, update this mapping; no role-based logic in the app.
 */
export const ROUTE_PERMISSIONS: Record<string, RoutePermission> = {
  '/companies': { module: 'companies', action: 'read' },
  '/companies/setup-wizard': { module: 'import_setup', action: 'read' },
  // Workspace document library is admin-tier: require 'read'. Staff (who only have
  // 'read_company'/'read_self') see only the "My Documents" tab (/my-documents).
  '/documents': { module: 'documents', action: 'read' },
  '/candidates': { module: 'users', action: 'read' },
  '/payroll': { module: 'payroll', action: 'read_org' },
  '/users': { module: 'users', action: 'read' },
  '/members': { module: 'company_members', action: 'read' },
  // People & HR opens for any HR grant; each tab re-checks its own action.
  '/hr': {
    module: 'hr',
    actions: [
      'directory',
      'employment',
      'structure',
      'movements',
      'probation',
      'documents',
      'policies',
      'discipline',
      'grievances',
    ],
  },
  '/roles': { module: 'roles', action: 'read' },
  '/settings/permissions-matrix': { module: 'roles', action: 'read' },
  '/audit-logs': { module: 'audit', action: 'read' },
  '/reports': { module: 'reports', action: 'read' },
  '/setup-wizard': { module: 'import_setup', action: 'read' },
  '/leave': { module: 'leave', action: 'read' },
  '/organization': { module: 'settings', action: 'read' },
  // What a company pays and when its card failed are commercial facts about the
  // business, not something every staff member should reach. Same permission the
  // API gates the billing endpoints with, so the two cannot disagree.
  // Changing the plan needs settings:update on the API, and every role can read
  // settings — so gate on update, or every employee sees prices and buttons that fail.
  '/billing': { module: 'settings', action: 'update' },
  '/announcements': { module: 'announcements', action: 'read' },
  '/shifts': { module: 'shifts', action: 'read_team' },
  '/attendance': { module: 'attendance', action: 'read_team' },
  '/time-corrections': { module: 'time_corrections', action: 'read' },
  '/leaves': { module: 'leave', action: 'read' },
  '/penalties': { module: 'penalties', action: 'read' },
  '/bonuses': { module: 'bonuses', action: 'read' },
  // Expense claims: staff self-service and the approvals inbox. Listed apart from
  // the /expenses ledger, which needs expenses:read and would hide them from staff.
  '/my-expenses': { module: 'expenses', action: 'read_self' },
  '/expense-approvals': { module: 'expenses', action: 'approve' },
  '/expenses': { module: 'expenses', action: 'read' },
  '/expense-analytics': { module: 'expenses', action: 'read' },
  '/security-ledger': { module: 'security_ledger', action: 'read' },
  '/wallboard': { module: 'wallboard', action: 'access' },
  '/boards': { module: 'boards', action: 'read' },
  '/channels': { module: 'channels', action: 'read' },
};

export function getPermissionForPath(path: string): RoutePermission | undefined {
  if (path === '/companies') {
    return { module: 'companies', action: 'read' };
  }
  if (
    path === '/companies/new' ||
    path === '/companies/setup-wizard/new' ||
    path === '/companies/setup-wizard/drafts/new'
  ) {
    return {
      module: '__all__',
      allOf: [
        { module: 'companies', action: 'create' },
        { module: 'import_setup', action: 'read' },
        { module: 'import_setup', action: 'update' },
      ],
    };
  }
  if (path === '/companies/setup-wizard' || path.startsWith('/companies/setup-wizard')) {
    return { module: 'import_setup', action: 'read' };
  }
  if (path.startsWith('/companies/')) {
    const afterCompanies = path.slice('/companies/'.length);
    const parts = afterCompanies.split('/');
    const companyId = parts[0];
    const subPath = parts.slice(1).join('/');
    if (!companyId || companyId === '') return { module: 'companies', action: 'read' };
    if (!subPath) return { module: 'companies', action: 'read' };
    if (subPath === 'setup-wizard' || subPath.startsWith('setup-wizard/'))
      return { module: 'import_setup', action: 'read' };
    if (subPath === 'attendance' || subPath.startsWith('attendance/'))
      return { module: 'attendance', action: 'read' };
    if (subPath === 'shifts' || subPath.startsWith('shifts'))
      return {
        module: 'shifts',
        actions: ['read_team', 'create', 'update', 'delete', 'bulk_assign'],
      };
    if (subPath === 'leaves' || subPath.startsWith('leaves'))
      return { module: 'leave', action: 'read' };
    if (subPath === 'penalties' || subPath.startsWith('penalties'))
      return { module: 'penalties', action: 'read' };
    if (subPath === 'bonuses' || subPath.startsWith('bonuses'))
      return { module: 'bonuses', action: 'read' };
    if (subPath === 'security-ledger' || subPath.startsWith('security-ledger'))
      return { module: 'security_ledger', action: 'read' };
    if (subPath === 'payroll' || subPath.startsWith('payroll'))
      return { module: 'payroll', action: 'read_company' };
    if (subPath === 'documents' || subPath.startsWith('documents'))
      return { module: 'documents', actions: ['read', 'read_company', 'read_staff_others'] };
    return { module: 'companies', action: 'read' };
  }
  if (path.startsWith('/payroll/me')) return { module: 'payroll', action: 'read_self' };
  if (path.startsWith('/payroll')) return { module: 'payroll', action: 'read_org' };
  if (path.startsWith('/tax-rules')) return { module: 'tax_rules', action: 'read' };
  if (path === '/my-shifts' || path.startsWith('/my-shifts')) return undefined; // self-service
  // Letters to read and sign: the API limits each one to its subject and signers.
  if (path === '/my-letters' || path.startsWith('/my-letters/')) return undefined;
  // Policies to read and acknowledge: the API limits each one to its employee.
  if (path === '/my-policies' || path.startsWith('/my-policies/')) return undefined;
  // HR cases shared with the caller: the API answers not-found for any other case.
  if (path === '/my-hr-cases' || path.startsWith('/my-hr-cases/')) return undefined;
  if (path === '/my-attendance' || path.startsWith('/my-attendance'))
    return { module: 'attendance', action: 'read_self' };
  if (path === '/my-time-corrections' || path.startsWith('/my-time-corrections'))
    return { module: 'time_corrections', action: 'read_self' };
  if (path === '/my-leaves' || path.startsWith('/my-leaves'))
    return { module: 'leave', action: 'read_self' };
  if (path === '/my-bonuses' || path.startsWith('/my-bonuses'))
    return { module: 'bonuses', action: 'read_self' };
  if (path === '/my-deposits' || path.startsWith('/my-deposits'))
    return { module: 'security_ledger', action: 'read_self' };
  if (path === '/my-documents' || path.startsWith('/my-documents'))
    return { module: 'documents', action: 'read_self' };
  if (path === '/candidates' || path.startsWith('/candidates'))
    return { module: 'users', action: 'read' };
  if (path === '/shifts' || path.startsWith('/shifts'))
    return { module: 'shifts', action: 'read_team' };
  if (path === '/time-corrections' || path.startsWith('/time-corrections'))
    return { module: 'time_corrections', action: 'read' };
  if (path === '/attendance' || path.startsWith('/attendance'))
    return { module: 'attendance', action: 'read_team' };
  if (path === '/leaves' || path.startsWith('/leaves')) return { module: 'leave', action: 'read' };
  if (path === '/penalties' || path.startsWith('/penalties'))
    return { module: 'penalties', action: 'read' };
  if (path === '/bonuses' || path.startsWith('/bonuses'))
    return { module: 'bonuses', action: 'read' };
  if (path === '/my-expenses' || path.startsWith('/my-expenses/'))
    return { module: 'expenses', action: 'read_self' };
  if (path === '/expense-approvals' || path.startsWith('/expense-approvals/'))
    return { module: 'expenses', action: 'approve' };
  if (path === '/expense-analytics' || path.startsWith('/expense-analytics'))
    return { module: 'expenses', action: 'read' };
  if (path === '/expenses' || path.startsWith('/expenses'))
    return { module: 'expenses', action: 'read' };
  if (path === '/hr' || path.startsWith('/hr/')) return ROUTE_PERMISSIONS['/hr'];
  if (path === '/members' || path.startsWith('/members'))
    return { module: 'company_members', action: 'read' };
  if (path === '/security-ledger' || path.startsWith('/security-ledger'))
    return { module: 'security_ledger', action: 'read' };
  if (path === '/wallboard' || path.startsWith('/wallboard'))
    return { module: 'wallboard', action: 'access' };
  if (path === '/boards' || path.startsWith('/boards')) return { module: 'boards', action: 'read' };
  if (path === '/channels' || path.startsWith('/channels'))
    return { module: 'channels', action: 'read' };
  if (path === '/setup-wizard' || path.startsWith('/setup-wizard'))
    return { module: 'import_setup', action: 'read' };
  return ROUTE_PERMISSIONS[path];
}
